/**
 * API CLIENT
 * Centralized axios instance with auth token injection
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_URL });

// Inject auth token on every request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('cr_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Handle 401 globally — redirect to login (skip for login/register requests)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = error.config?.url || '';
            // Don't redirect on login/register failures — let the component handle the error
            if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
                localStorage.removeItem('cr_token');
                localStorage.removeItem('cr_user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// =============================================
// AUTH
// =============================================
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (email, password) => api.post('/auth/login', { email, password }),
    verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
    resendVerification: (email) => api.post('/auth/resend-verification', { email }),
    verifyAdminMfa: (email, code) => api.post('/auth/admin-mfa-verify', { email, code }),
    getMe: () => api.get('/auth/me'),
    updateMe: (data) => api.put('/auth/me', data),
    deleteMe: () => api.delete('/auth/me'),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (email, code, password) => api.post('/auth/reset-password', { email, code, password })
};

// =============================================
// ROADS
// =============================================
export const roadsAPI = {
    getAll: () => api.get('/roads'),
    getBySlug: (slug) => api.get(`/roads/${slug}`)
};

// =============================================
// VENUES
// =============================================
export const venuesAPI = {
    getAll: (road) => api.get('/venues', { params: { road } }),
    getBySlug: (slug) => api.get(`/venues/${slug}`),
    verifyCheckin: (token) => api.get(`/venues/checkin/verify/${token}`),
    submitCheckin: (venue_token, lat, lng) =>
        api.post('/venues/checkin', { venue_token, lat, lng }),
    simulateCheckin: (venue_id) =>
        api.post('/venues/checkin/simulate', { venue_id }),
    getUserCheckins: () => api.get('/venues/user/checkins')
};

// =============================================
// PARTNERS
// =============================================
export const partnersAPI = {
    getAll: (road) => api.get('/partners', { params: { road } })
};

// =============================================
// ADMIN
// =============================================
export const adminAPI = {
    getStats: () => api.get('/admin/stats'),

    // Venues
    getVenues: () => api.get('/admin/venues'),
    createVenue: (data) => api.post('/admin/venues', data),
    updateVenue: (id, data) => api.put(`/admin/venues/${id}`, data),
    deleteVenue: (id) => api.delete(`/admin/venues/${id}`),
    getVenueQR: (id) => api.get(`/admin/venues/${id}/qr`),
    regenerateVenueQR: (id) => api.post(`/admin/venues/${id}/regenerate-qr`),

    // Roads
    getRoads: () => api.get('/admin/roads'),
    createRoad: (data) => api.post('/admin/roads', data),
    updateRoad: (id, data) => api.put(`/admin/roads/${id}`, data),

    // Partners
    getPartners: () => api.get('/admin/partners'),
    createPartner: (data) => api.post('/admin/partners', data),
    updatePartner: (id, data) => api.put(`/admin/partners/${id}`, data),
    deletePartner: (id) => api.delete(`/admin/partners/${id}`),

    // Users
    getUsers: (search) => api.get('/admin/users', { params: { search } }),
    markTshirtShipped: (id) => api.post(`/admin/users/${id}/tshirt-shipped`)
};

export default api;
